import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PhotoUploadModal, { TImage } from '../Feed/PhotoUploadModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../../pages/BrandDashboard/brandDashboardTokens';

type PictureUploaderProps = {
    initialImageSrc?: string | TImage[];
    onChange: (image: string | TImage[] | null, rawFile?: File[] | FileList | null, altenateKey?: string) => void;
    onRemove: () => void;
    sx?: object;
    multiple?: boolean;
    aspect?: number; // Custom crop ratio
    cropShape?: 'rect' | 'round';
    /** Width & height of the upload preview (px). Default 200. */
    size?: number;
    isEditMode?: boolean;
    maxCount?: number;
    supportedTypes?: ('image' | 'video')[];
    /** Match brand audition / company modal upload zones */
    brandChrome?: boolean;
    cropModalTitle?: string;
};

const PictureUploader: React.FC<PictureUploaderProps> = ({
    initialImageSrc,
    onChange,
    onRemove,
    sx = {},
    multiple,
    aspect,
    cropShape = 'rect',
    size = 200,
    isEditMode = true,
    maxCount = 1,
    supportedTypes,
    brandChrome = false,
    cropModalTitle,
}) => {
    const [imageSrc, setImageSrc] = useState<string | TImage[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { themeStyle } = useThemeContext();

    // Cropper modal state
    const [cropperOpen, setCropperOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [, setPendingFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Open cropper modal for single image mode
            if (!multiple) {
                setSrc(URL.createObjectURL(file));
                setPendingFile(file);
                setCropperOpen(true);
            } else {
                // For multiple, keep old behavior
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    setImageSrc(result);
                    onChange(result, [file], 'singleRawFile');
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleUpload = () => {
        if (!multiple && fileInputRef.current) {
            fileInputRef.current.click();
        } else {
            onChange(imageSrc);
        }
    };

    const handleRemove = () => {
        setImageSrc(null);
        onRemove();
        onChange(null); // Notify parent component that the image has been removed
    };

    // Compress image utility
    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    // Handle cropper save
    const handleCropSave = async (base64: string, file: File) => {
        setCropperOpen(false);
        setSrc('');
        setPendingFile(null);
        // Compress the cropped image
        const compressed = await compressImage(base64, 0.7);
        setImageSrc(compressed);
        onChange(compressed, [file], 'singleRawFile');
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        setSrc('');
        setPendingFile(null);
    };

    useEffect(() => {
        if (initialImageSrc) {
            setImageSrc(initialImageSrc);
        }
    }, [initialImageSrc]);

    if (multiple) {
        return (
            <PhotoUploadModal
                onCancelMediaUpload={() => {
                    onChange(null, null);
                    handleRemove();
                }}
                initialValue={initialImageSrc as TImage[]}
                onChange={(item, rawFiles) => {
                    onChange(item, rawFiles);
                }}
                maxCount={maxCount}
                supportedTypes={supportedTypes}
                sx={
                    brandChrome
                        ? {
                              border: 'none',
                              backgroundColor: BRAND_SURFACE_CONTAINER_HIGHEST,
                          }
                        : undefined
                }
                emptyStateSx={
                    brandChrome
                        ? {
                              borderRadius: '12px',
                              border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
                              bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                              transition: 'all 0.2s',
                              '&:hover': {
                                  borderColor: `${BRAND_LUXE_PRIMARY}60`,
                                  bgcolor: 'rgba(255,201,101,0.04)',
                              },
                          }
                        : undefined
                }
            />
        );
    }

    const isRound = cropShape === 'round';
    const iconSize = Math.max(22, Math.round(size * 0.36));

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: isRound ? 'center' : undefined,
                ...sx,
            }}>
            {/* Hidden file input for single image upload */}
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            {/* Picture Upload Box */}
            <Box
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                    borderRadius: isRound ? '50%' : 2,
                    border: brandChrome ? `2px dashed ${BRAND_OUTLINE_VARIANT}80` : '1px dashed',
                    borderColor: brandChrome ? undefined : 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: imageSrc
                        ? 'transparent'
                        : brandChrome
                        ? BRAND_SURFACE_CONTAINER_HIGHEST
                        : theme[themeStyle].secondaryBg,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    cursor: 'pointer',
                    flexShrink: 0,
                    ...(brandChrome && {
                        '&:hover': {
                            borderColor: `${BRAND_LUXE_PRIMARY}99`,
                            bgcolor: imageSrc ? 'transparent' : 'rgba(255,201,101,0.04)',
                        },
                    }),
                }}
                onClick={handleUpload}>
                {imageSrc ? (
                    <img
                        src={imageSrc as string}
                        alt="Uploaded"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: isRound ? 'top' : 'center',
                            borderRadius: isRound ? '50%' : undefined,
                        }}
                    />
                ) : (
                    <ImageIcon
                        sx={{
                            fontSize: iconSize,
                            color: brandChrome ? 'rgba(255,255,255,0.25)' : 'grey.500',
                        }}
                    />
                )}

                {imageSrc && isEditMode && (
                    <IconButton
                        onClick={e => {
                            e.stopPropagation();
                            handleRemove();
                        }}
                        sx={{
                            position: 'absolute',
                            top: isRound ? 2 : 8,
                            right: isRound ? 2 : 8,
                            width: isRound ? 22 : undefined,
                            height: isRound ? 22 : undefined,
                            backgroundColor: brandChrome ? 'rgba(0,0,0,0.65)' : 'white',
                            color: brandChrome ? '#fff' : undefined,
                            boxShadow: 1,
                            '&:hover': {
                                backgroundColor: brandChrome ? 'rgba(0,0,0,0.8)' : 'grey.200',
                            },
                        }}>
                        <CloseIcon sx={{ fontSize: isRound ? 14 : undefined }} />
                    </IconButton>
                )}
            </Box>
            {/* Cropper Modal for single image mode */}
            <RectangularImageCropperModal
                open={cropperOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={aspect ?? (isRound ? 1 : 16 / 9)}
                cropShape={cropShape}
                cropHeight={isRound ? 280 : undefined}
                title={cropModalTitle ?? (isRound ? 'Crop headshot' : undefined)}
            />
        </Box>
    );
};

export default PictureUploader;
